'use client'

import { environment } from 'src/environment'
import { isAuthedRoute } from '@lib/routes'
import { useAuth } from '@lib/context/auth'
import { usePathname } from 'next/navigation'
import React, { Suspense, useEffect } from 'react'
import Script from 'next/script'

declare global {
  interface Window {
    Beacon?: (method: string, options?: Record<string, unknown>) => void
  }
}

// We don't want to show Helpscout for Datadog E2E tests.
const isDatadogSyntheticUser = (id: string, email?: string) => {
  const datadogIds = new Set([
    'a0143bc3-97ed-4e99-8e9a-ff828a9bc716', // Staging Admin
    '4ab637c1-62f7-4d82-9418-6b274d938feb', // Staging User
    '01e64ae2-c289-4887-bc49-e05a8d9c7cb5', // Production Admin
    'ff5feca8-9a5f-420e-843b-abbb28ea680f', // Production User
  ])

  return (datadogIds.has(id) || email?.endsWith('@synthetics.dtdg.co')) ?? false
}

export const Beacon: React.FC = () => {
  const pathname = usePathname()
  const isAuthedPath = isAuthedRoute(pathname)
  const { user } = useAuth()

  const loadBeacon = user && !isDatadogSyntheticUser(user.id, user.email)

  useEffect(() => {
    if (window.Beacon && isAuthedPath && loadBeacon) {
      window.Beacon('identify', {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      })
    }
  }, [user, isAuthedPath, loadBeacon])

  if (!loadBeacon) {
    return null
  }

  return (
    <Suspense fallback={null}>
      {isAuthedPath && [
        <Script
          key="helpscout-beacon"
          strategy="lazyOnload"
          id="helpscout-beacon"
          dangerouslySetInnerHTML={{
            __html:
              '!function(e,t,n){function a(){var e=t.getElementsByTagName("script")[0],n=t.createElement("script");n.type="text/javascript",n.async=!0,n.src="https://beacon-v2.helpscout.net",e.parentNode.insertBefore(n,e)}if(e.Beacon=n=function(t,n,a){e.Beacon.readyQueue.push({method:t,options:n,data:a})},n.readyQueue=[],"complete"===t.readyState)return a();e.attachEvent?e.attachEvent("onload",a):e.addEventListener("load",a,!1)}(window,document,window.Beacon||function(){});',
          }}
        />,
        <Script
          key="helpscout-beacon-init"
          strategy="lazyOnload"
          id="helpscout-beacon-init"
          dangerouslySetInnerHTML={{
            __html: `window.Beacon("init", "${environment.NEXT_PUBLIC_HELPSCOUT_BEACON_ID}")`,
          }}
        />,
      ]}
    </Suspense>
  )
}
